import { createFileRoute, Link } from "@tanstack/react-router";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { KpiCard } from "@/components/kpi-card";
import { organizations, platformKpis, platformMrrSeries, fmtCents, auditEvents } from "@/mocks/saas-data";

export const Route = createFileRoute("/admin/")({
  ssr: false,
  head: () => ({ meta: [{ title: "Admin — Overview" }] }),
  component: AdminOverview,
});

function AdminOverview() {
  const user = useRequireRole(["superadmin"]);
  if (!user) return null;
  const max = Math.max(...platformMrrSeries.map((p) => p.v));
  const recent = auditEvents.slice(0, 6);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Platform overview" description="SaaS health at a glance." />

      <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Total Orgs" value={String(platformKpis.totalOrgs)} hint={`${platformKpis.activeOrgs30d} active 30d`} />
        <KpiCard label="MRR" value={fmtCents(platformKpis.mrrCents)} hint={`ARR ${fmtCents(platformKpis.arrCents)}`} accent />
        <KpiCard label="New signups (30d)" value={String(platformKpis.signups30d)} hint={`${platformKpis.churn30d} churned`} />
        <KpiCard label="Storage" value={`${platformKpis.storageGb} GB`} hint={`${platformKpis.aiCallsMonth.toLocaleString()} AI calls/mo`} />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <section className="rounded-xl bg-card p-6 ring-1 ring-border lg:col-span-2">
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">MRR (6 months)</h2>
          <div className="flex h-40 items-end gap-4">
            {platformMrrSeries.map((p) => (
              <div key={p.m} className="flex flex-1 flex-col items-center gap-1">
                <div className="w-full rounded-t bg-foreground" style={{ height: `${(p.v / max) * 100}%` }} />
                <span className="font-mono text-[10px] text-muted-foreground">{p.m}</span>
                <span className="font-mono text-[10px] font-medium">{fmtCents(p.v * 100)}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl bg-card ring-1 ring-border">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Recent audit</h2>
            <Link to="/admin/audit" className="text-xs font-medium text-brand hover:underline">View all</Link>
          </div>
          <ul className="divide-y divide-border">
            {recent.map((e) => (
              <li key={e.id} className="px-5 py-3 text-xs">
                <p className="font-mono">{e.action}</p>
                <p className="text-muted-foreground">{e.actor} · {new Date(e.at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Top organizations</h2>
          <Link to="/admin/organizations" className="text-xs font-medium text-brand hover:underline">All organizations</Link>
        </div>
        <div className="overflow-hidden rounded-xl bg-card ring-1 ring-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-muted/50">
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Organization</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Seats</th>
                <th className="px-4 py-3 font-medium">Storage</th>
                <th className="px-4 py-3 text-right font-medium">MRR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {organizations.map((o) => (
                <tr key={o.id}>
                  <td className="px-4 py-3">
                    <Link to="/admin/organizations/$id" params={{ id: o.id }} className="font-medium hover:underline">
                      {o.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{o.planId}</td>
                  <td className="px-4 py-3 font-mono text-[10px] uppercase">{o.status}</td>
                  <td className="px-4 py-3 font-mono">{o.seats}</td>
                  <td className="px-4 py-3 font-mono text-muted-foreground">{o.storageUsedGb} GB</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtCents(o.mrrCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
